import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Fab,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { Add as AddIcon, GroupAdd as JoinIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Team } from '../../types';
import { teamService } from '../../services/teamService';
import CreateTeamDialog from './CreateTeamDialog';
import TeamCard from './TeamCard';
import JoinTeamDialog from './JoinTeamDialog';
import ChallengesMissionsPage from '../challenges/ChallengesMissionsPage';
import PrizesPage from '../prizes/PrizesPage';
import CoachAvatarShopPage from '../shop/CoachAvatarShopPage';
import TeamAnalyticsTab from './TeamAnalyticsTab';

const TeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // For coach tabs
  const { user, updateUserTeamId } = useAuth();

  useEffect(() => {
    console.log('TeamsPage: useEffect - User role:', user?.role, 'User ID:', user?.id);
    console.log('TeamsPage: Full user object:', user);
    console.log('TeamsPage: user?.id type:', typeof user?.id, 'value:', user?.id);
    console.log('TeamsPage: user object keys:', user ? Object.keys(user) : []);
    
    // CRITICAL FIX: If user.id is missing, try to get it from Firebase Auth
    if (user && !user.id) {
      console.warn('TeamsPage: user.id is missing! Attempting to get from Firebase Auth...');
      // Import auth to get current user
      import('../../firebase/config').then(({ auth }) => {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid) {
          console.log('TeamsPage: Found Firebase Auth user.uid:', currentUser.uid);
          // Update the user object with the id
          const updatedUser = {
            ...user,
            id: currentUser.uid
          };
          console.log('TeamsPage: Updated user object with id:', updatedUser);
          // We can't directly update the user from AuthContext here, but we can use the uid directly
          // For now, let's use currentUser.uid directly in the loadTeams function
          if (user.role === 'coach' || user.role === 'headCoach' || user.role === 'athlete') {
            loadTeamsWithUserId(currentUser.uid);
            return;
          }
        } else {
          console.error('TeamsPage: Firebase Auth currentUser is also null!');
        }
      });
      
      setLoading(false);
      return;
    }
    
    if (user && user.id && (user.role === 'coach' || user.role === 'headCoach' || user.role === 'athlete')) {
      loadTeams();
    } else {
      console.warn('TeamsPage: Cannot load teams - user or user.id missing', { 
        user, 
        hasUser: !!user, 
        hasId: !!user?.id, 
        userId: user?.id,
        userKeys: user ? Object.keys(user) : []
      });
      setLoading(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to load teams with explicit userId (fallback when user.id is missing)
  const loadTeamsWithUserId = async (userId: string) => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      console.log('TeamsPage: loadTeamsWithUserId called with userId:', userId);
      let userTeams: Team[];
      if (user.role === 'coach' || user.role === 'headCoach') {
        console.log('TeamsPage: Loading teams for coach/headCoach, userId:', userId, 'role:', user.role);
        
        // First ensure coach/head coach is added as member of their own teams
        try {
          await teamService.ensureCoachMembership(userId);
          console.log('TeamsPage: ensureCoachMembership completed');
        } catch (ensureError) {
          console.warn('TeamsPage: Error in ensureCoachMembership (non-critical):', ensureError);
        }
        
        // For coaches and head coaches, check both coach-owned teams and team memberships
        console.log('TeamsPage: Querying for coach teams and member teams...');
        const [coachTeams, memberTeams] = await Promise.all([
          teamService.getTeamsByCoach(userId),
          teamService.getTeamsByAthlete(userId)
        ]);
        
        console.log('TeamsPage: Query results - coachTeams:', coachTeams.length, 'memberTeams:', memberTeams.length);
        console.log('TeamsPage: Coach teams details:', coachTeams.map(t => ({ id: t.id, name: t.name, headCoachId: t.headCoachId })));
        console.log('TeamsPage: Member teams details:', memberTeams.map(t => ({ id: t.id, name: t.name })));
        
        // Combine and deduplicate teams
        const allTeams = [...coachTeams];
        memberTeams.forEach(memberTeam => {
          if (!allTeams.find(team => team.id === memberTeam.id)) {
            allTeams.push(memberTeam);
          }
        });
        
        userTeams = allTeams;
        console.log('TeamsPage: Coach/Head Coach teams found:', coachTeams.length, 'member teams:', memberTeams.length, 'total:', userTeams.length);
        
        if (userTeams.length === 0) {
          console.warn('TeamsPage: No teams found for head coach! This might indicate:');
          console.warn('  1. Team document missing headCoachId field');
          console.warn('  2. headCoachId does not match user.id');
          console.warn('  3. Firestore query error (check console for details)');
          console.warn('  4. Missing Firestore index');
        }
      } else if (user.role === 'athlete') {
        userTeams = await teamService.getTeamsByAthlete(userId);
      } else {
        userTeams = [];
      }
      setTeams(userTeams);
    } catch (err: any) {
      console.error('TeamsPage: Error loading teams:', err);
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    // Get userId - try user.id first, fallback to Firebase Auth
    let userId = user?.id;
    
    if (!userId && user) {
      console.warn('TeamsPage: user.id is missing, attempting to get from Firebase Auth...');
      try {
        const { auth } = await import('../../firebase/config');
        if (auth.currentUser?.uid) {
          userId = auth.currentUser.uid;
          console.log('TeamsPage: Got userId from Firebase Auth:', userId);
        }
      } catch (error) {
        console.error('TeamsPage: Error getting userId from Firebase Auth:', error);
      }
    }
    
    if (!user || !userId) {
      console.warn('TeamsPage: User or userId is missing', { user, userId });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let userTeams: Team[];
      if (user.role === 'coach' || user.role === 'headCoach') {
        console.log('TeamsPage: Loading teams for coach/headCoach, userId:', userId, 'role:', user.role);
        
        // First ensure coach/head coach is added as member of their own teams
        try {
          await teamService.ensureCoachMembership(userId);
          console.log('TeamsPage: ensureCoachMembership completed');
        } catch (ensureError) {
          console.warn('TeamsPage: Error in ensureCoachMembership (non-critical):', ensureError);
        }
        
        // For coaches and head coaches, check both coach-owned teams and team memberships
        console.log('TeamsPage: Querying for coach teams and member teams...');
        const [coachTeams, memberTeams] = await Promise.all([
          teamService.getTeamsByCoach(userId),
          teamService.getTeamsByAthlete(userId)
        ]);
        
        console.log('TeamsPage: Query results - coachTeams:', coachTeams.length, 'memberTeams:', memberTeams.length);
        console.log('TeamsPage: Coach teams details:', coachTeams.map(t => ({ id: t.id, name: t.name, headCoachId: t.headCoachId })));
        console.log('TeamsPage: Member teams details:', memberTeams.map(t => ({ id: t.id, name: t.name })));
        
        // Combine and deduplicate teams
        const allTeams = [...coachTeams];
        memberTeams.forEach(memberTeam => {
          if (!allTeams.find(team => team.id === memberTeam.id)) {
            allTeams.push(memberTeam);
          }
        });
        
        userTeams = allTeams;
        console.log('TeamsPage: Coach/Head Coach teams found:', coachTeams.length, 'member teams:', memberTeams.length, 'total:', userTeams.length);
        
        if (userTeams.length === 0) {
          console.warn('TeamsPage: No teams found for head coach! This might indicate:');
          console.warn('  1. Team document missing headCoachId field');
          console.warn('  2. headCoachId does not match user.id');
          console.warn('  3. Firestore query error (check console for details)');
          console.warn('  4. Missing Firestore index');
        }
      } else if (user.role === 'athlete') {
        userTeams = await teamService.getTeamsByAthlete(userId);
      } else {
        userTeams = [];
      }
      setTeams(userTeams);
    } catch (err: any) {
      console.error('TeamsPage: Error loading teams:', err);
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamCreated = () => {
    loadTeams();
  };

  const handleTeamUpdated = () => {
    loadTeams();
  };

  const handleTeamDeleted = () => {
    loadTeams();
  };

  const handleTeamJoined = async (teamId: string) => {
    console.log('TeamsPage: Team joined, teamId:', teamId);
    // Reload teams list
    loadTeams();
    
    // Update user's teamId using AuthContext method
    updateUserTeamId(teamId);
    console.log('TeamsPage: Updated user.teamId to:', teamId);
  };

  if (user?.role === 'athlete') {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, mt: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3 
        }}>
          <Typography variant="h4" component="h1">
            Team Rosters
          </Typography>
          <Button
            variant="contained"
            startIcon={<JoinIcon />}
            onClick={() => setJoinDialogOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Join Team
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={60} />
          </Box>
        ) : teams.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No teams yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ask your coach for an invitation code to join their team.
            </Typography>
            <Button
              variant="contained"
              startIcon={<JoinIcon />}
              onClick={() => setJoinDialogOpen(true)}
              size="large"
            >
              Join Your First Team
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {teams.map((team) => (
              <Grid key={team.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <TeamCard
                  team={team}
                  onTeamUpdated={handleTeamUpdated}
                  onTeamDeleted={handleTeamDeleted}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <JoinTeamDialog
          open={joinDialogOpen}
          onClose={() => setJoinDialogOpen(false)}
          onTeamJoined={handleTeamJoined}
        />
      </Box>
    );
  }

  if (user?.role !== 'coach' && user?.role !== 'headCoach') {
    console.log('TeamsPage: Access denied. User role:', user?.role, 'User ID:', user?.id);
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Only coaches, head coaches, and athletes can access teams.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, mt: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          Team Management
        </Typography>
        {currentTab === 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<JoinIcon />}
              onClick={() => setJoinDialogOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Join Team
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Create Team
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for coach team management */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Teams & Members" />
          <Tab label="Team Analytics" />
          <Tab label="Challenges & Missions" />
          <Tab label="The Shop" />
          <Tab label="Shop Items" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={60} />
            </Box>
          ) : teams.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No teams yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first team or join an existing team to start managing athletes and tracking their progress.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="outlined"
                  startIcon={<JoinIcon />}
                  onClick={() => setJoinDialogOpen(true)}
                  size="large"
                >
                  Join Team
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  size="large"
                >
                  Create Your First Team
                </Button>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {teams.map((team) => (
                <Grid key={team.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <TeamCard
                    team={team}
                    onTeamUpdated={handleTeamUpdated}
                    onTeamDeleted={handleTeamDeleted}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {currentTab === 1 && <TeamAnalyticsTab />}
      {currentTab === 2 && <ChallengesMissionsPage />}
      {currentTab === 3 && <PrizesPage />}
      {currentTab === 4 && <CoachAvatarShopPage />}

      {/* Floating Action Button for mobile */}
      {currentTab === 0 && (
        <Fab
          color="primary"
          aria-label="create team"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' },
          }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      <CreateTeamDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onTeamCreated={handleTeamCreated}
      />
      
      <JoinTeamDialog
        open={joinDialogOpen}
        onClose={() => setJoinDialogOpen(false)}
        onTeamJoined={handleTeamJoined}
      />
    </Box>
  );
};

export default TeamsPage;
